import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormBuilder, FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { PredictScheduleModelComponent } from './predict-schedule-model/predict-schedule-model.component';
import { strictDateValidator, dateRangeCompleteValidator } from '../shared/commonutils/helper';
import { Subject } from 'rxjs';
import { SelectSearchComponent } from "../shared/components/select-search/select-search.component";
import { MatSelectItems } from '../../interfaces/lookup';
import { LookUpService } from '../../services/lookupservice';
import { Regions, SitesShortForm } from '../../interfaces/regions';
import { PredictService } from '../../services/predict.service';
import { MLPredictFeedstockSchedule } from '../../interfaces/predictModalLoad';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatCheckboxModule } from "@angular/material/checkbox";
import { AlertDialogComponent } from '../shared/components/alert-dialog/alert-dialog.component';

@Component({
  selector: 'app-load-model',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatDialogModule,
    SelectSearchComponent,
    MatCheckboxModule
],
  templateUrl: './load-model.component.html',
  styleUrl: './load-model.component.scss'
})
export class LoadModelComponent implements OnInit {
  loadModelForm!: FormGroup;  
  range: FormGroup = new FormGroup({
    start: new FormControl<Date | null>({value:null, disabled: true}, [strictDateValidator]),
    end: new FormControl<Date | null>({value:null, disabled: true}, [strictDateValidator]),
  }, {validators: dateRangeCompleteValidator});
  predictWeeks: MatSelectItems[] = [
    { id: 1, name: '1 Week' },
    { id: 2, name: '2 Week' },
    { id: 3, name: 'Custom Week' }
  ];
  marketId: number = -1;
  weekId: number = -1;
  siteId: number = 0;
  marketData: MatSelectItems[] = [];
  // Replace this with your API response
  disposalSites: MatSelectItems[] = [];
  displayedColumns: string[] = ['day', 'customer', 'stream', 'tank',
    'hauler', 'uom', 'projectedQuantity', 'predictedGas', 'fat', 'carbs', 'protein'
  ];  
  private destroy$ = new Subject<void>();
  dataSource = new MatTableDataSource<any>;
  selectedRow: MLPredictFeedstockSchedule[] = [];
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;  

  constructor(private fb: FormBuilder, private dialog: MatDialog,
    private lookUp: LookUpService, private predictService: PredictService
  ){
  }

  ngOnInit(){
    this.initializeForm();
    this.range.valueChanges.subscribe(value => {
      this.loadModelForm.patchValue({
        startDate: value.start,
        endDate: value.end
      }, { emitEvent: false });
    });
  }

  initializeForm(): void {
    this.loadModelForm = this.fb.group({
      predictWeek: [''],
      startDate: [{ value: null }],
      endDate: [{ value: null }],
      disposalSiteId: [null]
    });
    this.lookUp.getMarketsList("").subscribe((result: Regions[]) => {
      this.marketData = result.map(item => ({
        id: item.id!,
        name: item.regionName!
      }));
    })
    this.predictService.getMLLoadModelRegionSites(this.marketId, 1).subscribe((result: SitesShortForm[]) => {
      this.disposalSites = result.map(item => ({
        id: item.id!,
        name: item.siteName!
      }));
    })
    this.predictService.getMLPredictFeedstockSchedule().subscribe((result : MLPredictFeedstockSchedule[]) => {
      this.selectedRow = [];
      this.dataSource.data = result;
      this.dataSource.paginator = this.paginator
      this.dataSource.sort = this.sort;
    })
  }

  onPredictWeekChange(value: number | null): void {
    if (value === 3) {
      this.enableCustomDates();
      return;
    }

    this.setAutomaticDates(Number(value));
  }

  setAutomaticDates(numberOfWeeks: number): void {
    const today = this.getTodayWithoutTime();

    const startDate = new Date(today);
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + (numberOfWeeks * 7) - 1);

    this.range.patchValue({
      start: startDate,
      end: endDate
    });
  
    // Disable manual editing for 1 Week / 2 Week
    this.range.get('start')?.disable({ emitEvent: false });
    this.range.get('end')?.disable({ emitEvent: false });
  
    // Keep your main form values in sync if you still need them
    this.loadModelForm.patchValue({
      startDate,
      endDate
    });
  }

  enableCustomDates(): void {
    this.range.get('start')?.enable();
    this.range.get('end')?.enable();

    this.range.reset({
      start: null,
      end: null
    });

    this.loadModelForm.patchValue({
      startDate: null,
      endDate: null
    });
  }

  private getTodayWithoutTime(): Date {
    const today = new Date();

    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
  }

  isSelected(row: MLPredictFeedstockSchedule){
    return this.selectedRow.some(x => x.id === row.id);
  }

  onRowSelectionChange(row: MLPredictFeedstockSchedule, checked: boolean): void {  
    if (checked) {
      if(!this.isSelected(row)){
        this.selectedRow.push(row);
      }
    } else {
      this.selectedRow = this.selectedRow.filter(x => x.id !== row.id);
    }
  }  

  onMarketReceived(event: number | null) {
    this.marketId = event ?? -1;
    this.predictService.getMLLoadModelRegionSites(this.marketId, 1).subscribe((result: SitesShortForm[]) => {
      this.disposalSites = result.map(item => ({
        id: item.id!,
        name: item.siteName!
      }));
    })
  }

  onDisposalSiteChange(id: number | null): void {
    this.siteId = id ?? 0;
  }

  onPredictLoad(): void {
    if (this.loadModelForm.invalid) {
      this.loadModelForm.markAllAsTouched();
      return;
    }

    const formValue = this.loadModelForm.getRawValue();

    if (!formValue.startDate || !formValue.endDate) {
      return;
    }

    if (formValue.startDate > formValue.endDate) {
      return;
    }

    if (this.range.invalid) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }

    if (this.range.invalid || !this.range.value.start || !this.range.value.end) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }

    if(this.siteId === null || this.siteId < 1){
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Disposal Site',
          message: 'Please select a Disposal Site.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;

    }

    const selectedDisposalSite = this.disposalSites.find(x => x.id === this.siteId);

    const dialogData: any = {
      fDate: formValue.startDate,
      tDate: formValue.endDate,
      siteId: this.siteId,
      disposalSiteName: selectedDisposalSite?.name ?? ''
    };

    const dialogRef = this.dialog.open(PredictScheduleModelComponent, {
      width: '1100px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: true,
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Selected predicted loads:', result);

        // Later:
        // Call your Confirm API here
        this.onPredictLoad();
      }
    });
  }

  onConfirm(): void {
    // This will be used after final API integration
  }

  get isPredictLoadDisabled(): boolean {
    const formValue = this.loadModelForm.getRawValue();

    return !formValue.predictWeek ||
      !formValue.startDate ||
      !formValue.endDate ||
      !formValue.disposalSiteId;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onKeydown(event: KeyboardEvent) {
    if(event.ctrlKey && event.metaKey){return;} 
    if(!/[0-9\/]/.test(event.key) && event.key.length === 1){
      event.preventDefault();
    }
    setTimeout(() => {
      this.range.updateValueAndValidity();
      this.range.markAllAsTouched();
    });
  }

  dateformat(event: Event){
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if(!value) return;
    const parts = value.split('/');
    if(parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if(month.length === 1)  month = '0' + month;
    if(day.length === 1)  day = '0' + day;
    if(year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }
}
